import {
  FX_BREAKLINE_TYPES,
  FxBreaklineType,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { BtnClasses, Button } from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import { Component, createMemo, createSignal, For } from 'solid-js';

import { ExtractionWorkflowSubtitle } from './ExtractionWorkflowSubtitle';

export const BreaklinesExtractionSettings: Component = () => {
  const fxWorkspace = useFxWorkspaceContext();
  const [selectedBreaklineTypes, setSelectedBreaklineTypes] = createSignal<
    ReadonlySet<FxBreaklineType>
  >(new Set());

  const activeWorkspaceId = createMemo(() => fxWorkspace.activeWorkspaceId());

  const isBuilding = createMemo(() => {
    const workspaceId = activeWorkspaceId();
    return (
      workspaceId !== undefined && fxWorkspace.isBreaklinesBuilding(workspaceId)
    );
  });

  const canRunExtraction = createMemo(
    () =>
      selectedBreaklineTypes().size > 0 &&
      activeWorkspaceId() !== undefined &&
      !isBuilding()
  );

  const isBreaklineTypeSelected = (type: FxBreaklineType): boolean =>
    selectedBreaklineTypes().has(type);

  const toggleBreaklineType = (type: FxBreaklineType): void => {
    setSelectedBreaklineTypes((previous) => {
      const next = new Set(previous);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const handleRun = (): void => {
    const workspaceId = activeWorkspaceId();
    if (!canRunExtraction() || workspaceId === undefined) {
      return;
    }

    void fxWorkspace.runBreaklinesExtraction(workspaceId, [
      ...selectedBreaklineTypes(),
    ]);
  };

  return (
    <div
      class="flex w-full flex-col gap-4"
      data-testid="breaklines-extraction-settings"
    >
      <div class="flex flex-col gap-2">
        <ExtractionWorkflowSubtitle />
        <p class="m-0 text-sm text-muted">
          {t('mobileMapping.fxWorkspace.extraction.breaklines.selectTypes')}
        </p>
        <div class="flex flex-col gap-2">
          <For each={FX_BREAKLINE_TYPES}>
            {(type) => (
              <modus-wc-checkbox
                size="md"
                class="shrink-0"
                attr:data-testid={`extraction-breakline-${type}`}
                input-id={`extraction-breakline-${type}`}
                value={isBreaklineTypeSelected(type)}
                label={t(`mobileMapping.fxWorkspace.breaklines.${type}`)}
                on:inputChange={() => toggleBreaklineType(type)}
              />
            )}
          </For>
        </div>
      </div>
      <Button
        color={BtnClasses.PRIMARY}
        icon="tc-icon-play"
        content={t('mobileMapping.fxWorkspace.extraction.run')}
        data-cy="extraction-workflow-run-button"
        class="self-start"
        disabled={!canRunExtraction()}
        onClick={handleRun}
      />
    </div>
  );
};
